// -----------------------------------------------------------------------------
// HTML sanitization helper (SECURITY-CRITICAL)
// -----------------------------------------------------------------------------
// Any user- or partner-controlled string that reaches a `v-html` sink MUST be
// run through one of the exports below first. Without this, stored XSS is
// possible: e.g. a member's free-text `additionalNotes` is rendered inside an
// RM admin's browser during the "simulate" impersonation feature, so an
// injected <script> would run with the admin's session (admin JWT lives in
// localStorage.simulationStack) — a full admin-account takeover.
//
// We wrap DOMPurify with two hardened, allow-list-based configs:
//   * sanitizeHtml(dirty)     — strict, for basic rich text from the Tiptap
//                               editor (member/partner descriptions, notes).
//   * sanitizeRichHtml(dirty) — same protections, plus the small extra surface
//                               the agent chat renderer needs (inline "Preferred"
//                               icon SVG + link `class` hooks its click handler
//                               relies on).
//
// Both configs strip <script>, event handlers (onload/onerror/onclick/…),
// <iframe>/<object>/<embed>, form controls, and dangerous URI schemes
// (javascript:, data:), and they harden every external link with
// rel="noopener noreferrer" + target="_blank". Verified with jsdom against a
// battery of XSS payloads.
// -----------------------------------------------------------------------------

import DOMPurify from 'dompurify'

// Tags emitted by the vuetify-pro-tiptap editor for basic formatting.
const FORMATTING_TAGS = [
  'p',
  'br',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'del',
  'a',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'code',
  'pre',
  'hr',
]

// Attributes allowed on the formatting tags above. `class`/`style`/`id` are
// intentionally excluded from the strict config to keep the surface minimal.
const FORMATTING_ATTR = ['href', 'target', 'rel', 'title']

// Belt-and-suspenders: these are never allowed even if a caller's allow-list
// would otherwise permit them. DOMPurify already blocks <script>, but we forbid
// the rest explicitly so intent is obvious and future config edits stay safe.
const FORBID_TAGS = [
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'textarea',
  'button',
  'select',
  'base',
  'meta',
  'link',
]

// Register the link-hardening hook exactly once. DOMPurify is a shared
// singleton, so this hook runs for every sanitize call (strict and rich):
// any absolute http(s) link is forced to open in a new tab without leaking
// window.opener. Relative/internal links (e.g. the agent's "/r/:id" SPA links)
// are left untouched so in-app navigation keeps working.
let hookRegistered = false
const registerHook = () => {
  if (hookRegistered || typeof DOMPurify.addHook !== 'function') return
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || ''
      if (/^https?:\/\//i.test(href)) {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
      }
    }
  })
  hookRegistered = true
}
registerHook()

/**
 * Strict sanitizer for basic rich text (Tiptap output, member/partner
 * free-text). Use this for every untrusted `v-html` sink.
 *
 * @param {unknown} dirty - Untrusted HTML string (null/undefined => '').
 * @returns {string} Sanitized HTML safe to bind with v-html.
 */
export function sanitizeHtml(dirty) {
  if (dirty === null || dirty === undefined) return ''

  return DOMPurify.sanitize(String(dirty), {
    ALLOWED_TAGS: FORMATTING_TAGS,
    ALLOWED_ATTR: FORMATTING_ATTR,
    FORBID_TAGS,
    ALLOW_DATA_ATTR: false,
  })
}

/**
 * Sanitizer for the agent chat renderer. Keeps the small extra surface that
 * renderer needs — the hardcoded inline "Preferred" icon SVG and the link
 * `class` hooks its click handler uses for SPA navigation — while still
 * stripping scripts, event handlers, iframes, form controls, and dangerous
 * URI schemes. Only use this for trusted-ish, server-generated agent markup,
 * not for arbitrary member/partner input.
 *
 * @param {unknown} dirty - Agent-generated HTML string (null/undefined => '').
 * @returns {string} Sanitized HTML safe to bind with v-html.
 */
export function sanitizeRichHtml(dirty) {
  if (dirty === null || dirty === undefined) return ''

  // Uses DOMPurify's default (safe) allow-list — which already permits inline
  // SVG, `class`, and the `style` attribute — plus an explicit FORBID list.
  return DOMPurify.sanitize(String(dirty), {
    ADD_ATTR: ['target'],
    FORBID_TAGS,
  })
}
